import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import api from "../api/api";
import ProvidersPopup from "./popups/providerspopup";
import StatusPopup from "./popups/statuspopup";
import RoomPopup from "./popups/roompopup";
import Payments from "./popups/Payments";
import DropdownMenu from "./popups/Menu";
import labsIcon from "../../src/assets/Signin/LabsIcon.svg";
import { printEncounterForPatient, fmtName, fmtTime } from "../printnotes/encounterData";
import {
  FLAGGED_STATUSES,
  checkinAt,
  formatApptTime,
  formatWait,
  providersOf,
  roomOf,
  statusMeta,
  waitMinutes,
} from "./signinmodel";
import { getPracticeId, signinSheetRules } from "../config/practice";

const AVATAR_TONES = [
  { background: "#eef0fb", color: "#3d4fa8" },
  { background: "#f7ecf8", color: "#8e3b95" },
  { background: "#fdf0e7", color: "#c2622b" },
  { background: "#e8f1fb", color: "#2f5d9e" },
  { background: "#e7f5ee", color: "#23734a" },
];
// Same patient, same colour on every refresh.
const avatarTone = (row) =>
  AVATAR_TONES[Math.abs(Number(row.patientid) || 0) % AVATAR_TONES.length];
const initials = (row) =>
  `${(row.firstname || "").trim()[0] || ""}${(row.lastname || "").trim()[0] || ""}`.toUpperCase() || "?";

const StethoscopeIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 3.5v5a4 4 0 0 0 8 0v-5" />
    <path d="M10 12.5v2.5a4.5 4.5 0 0 0 9 0v-1.5" />
    <circle cx="19" cy="11.5" r="2" />
  </svg>
);
const PulseIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="3 12 7.5 12 10 6.5 14 17.5 16.5 12 21 12" />
  </svg>
);
const PrinterIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 8.5V3.5h10v5" />
    <rect x="3.5" y="8.5" width="17" height="8" rx="2" />
    <path d="M7 14h10v6.5H7z" />
  </svg>
);
const ChevronDown = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const SortArrow = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 5v14M6 13l6 6 6-6" />
  </svg>
);

const tokenOf = (row) => row.token || Number.MAX_SAFE_INTEGER;
const SORTERS = {
  token: (a, b) => tokenOf(a) - tokenOf(b),
  patient: (a, b) =>
    `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`),
  appt: (a, b) => String(a.starttime || "").localeCompare(String(b.starttime || "")),
  checkin: (a, b) =>
    (checkinAt(a)?.valueOf() ?? Infinity) - (checkinAt(b)?.valueOf() ?? Infinity),
};

const SigninTable = ({
  rows,
  allRows,
  loading,
  setData,
  fetchdata,
  providersdata,
  setProvidersData,
  live,
  now,
}) => {
  const practiseid = getPracticeId();
  const homescreenprev = useSelector((state) => state.userPrivileges.homescreen);
  const navigate = useNavigate();
  const [isProvidersPopup, setIsprovidersPopup] = useState(false);
  const [isStatusPopup, setIsStatusPopup] = useState(false);
  const [roomPopUp, setRoomPopUp] = useState(false);
  const [paymentModal, setpaymentModal] = useState(false);
  const [rowdata, setRowdata] = useState({});
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [sort, setSort] = useState({ key: "token", dir: "asc" });
  // OSA batch 6: 7825 has no Print/Reprint column (C-HIM's encounter form;
  // OSA prints the BlinkScribe note PDF). Its "..." menu moves into the
  // Status cell. The empty-state row spans every column shown.
  const { printColumn } = signinSheetRules();
  const columnCount = 8 + (printColumn ? 1 : 0);

  const sorted = useMemo(() => {
    const list = [...rows];
    const cmp = SORTERS[sort.key];
    list.sort((a, b) => {
      // Default order, as before: by token with "Ready for Triage" last.
      if (sort.key === "token" && sort.dir === "asc") {
        const aT = a.visit_confirmation === "Ready for Triage";
        const bT = b.visit_confirmation === "Ready for Triage";
        if (aT !== bT) return aT ? 1 : -1;
      }
      return sort.dir === "asc" ? cmp(a, b) : cmp(b, a);
    });
    return list;
  }, [rows, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  // A plain function, not a component: a component defined in render would
  // remount the header cells on every render.
  const sortHead = (k, children) => (
    <th aria-sort={sort.key === k ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className={`bmsi-sort${sort.key === k ? ` on ${sort.dir}` : ""}`}
        onClick={() => toggleSort(k)}
      >
        {children}
        <SortArrow />
      </button>
    </th>
  );

  // Print Notes encounter form for one signed-in patient (uses the shared
  // printnotes data layer; the checkin row only carries name/patientid/provider).
  const handlePrintEncounter = async (e, item) => {
    e.stopPropagation();
    setPrintingId(item.patientid);
    try {
      await printEncounterForPatient({
        patientid: item.patientid,
        name: fmtName(item.lastname, item.firstname),
        provider: providersOf(item).join(", "),
        time: fmtTime(item.starttime),
        date: item.startdate ? item.startdate.split("T")[0] : "",
      });
      // Persist who printed + when. The server reads the username from the JWT
      // and stores it in the appointment's signintime JSON (no schema change).
      try {
        const res = await api.put(
          `/v1/${practiseid}/checkin/patient/${item.patientid}/mark-printed`,
          {
            appointment_id: item.appointmentid,
            appointment_date: item.startdate ? item.startdate.split("T")[0] : "",
          },
        );
        if (res && res.printedBy) {
          setData((prev) =>
            prev.map((row) =>
              row.appointmentid === item.appointmentid
                ? {
                    ...row,
                    signintime: {
                      ...(row.signintime || {}),
                      printedBy: res.printedBy,
                      printedAt: res.printedAt,
                      printCount: res.printCount,
                    },
                  }
                : row,
            ),
          );
        }
      } catch (markErr) {
        console.error("mark-printed failed", markErr);
      }
    } catch (err) {
      console.error("print encounter failed", err);
    } finally {
      setPrintingId(null);
    }
  };

  const updateRow = async (obj) => {
    try {
      await api.put(`/v1/${practiseid}/checkin/patient/${rowdata?.patientid}/update-data`, {
        appointment_id: rowdata?.appointmentid,
        appointment_date: rowdata?.startdate?.split("T")[0] || "",
        ...obj,
      });
      // update the UI straight away, without waiting for a reload
      setData((prev) =>
        prev.map((row) => (row.appointmentid === rowdata.appointmentid ? { ...row, ...obj } : row)),
      );
      setRowdata({ ...rowdata, ...obj });
    } catch (error) {
      console.error("Error updating row:", error);
    } finally {
      setIsprovidersPopup(false);
      setIsStatusPopup(false);
      fetchdata({ load: false });
    }
  };

  const getproviders = (providers) => updateRow({ linked_provider: providers });
  const updateStatus = (a) => updateRow({ visit_confirmation: String(a) });
  // Rooming moves a waiting visit to Ready for Provider; a checked-out visit
  // keeps its status (same rule as the dashboard's Assign Room modal), so the
  // Checked out view cannot reopen a finished visit.
  const updateRoom = (a) =>
    updateRow({
      room_no: a,
      visit_confirmation:
        String(rowdata?.visit_confirmation || "").trim() === "Checked Out" ? "Checked Out" : "Ready for Provider",
    });

  const openRoom = (item) => {
    setRowdata(item);
    setRoomPopUp(true);
  };
  const openStatus = (item) => {
    setRowdata(item);
    setIsStatusPopup(true);
  };
  const openProviders = (item) => {
    setRowdata(item);
    setProvidersData(providersOf(item));
    setIsprovidersPopup(true);
  };

  const ClosePaymentModal = () => {
    setpaymentModal(false);
    setSelectedPatientId(null);
  };
  const openPaymentModal = (item) => {
    setpaymentModal(true);
    setSelectedPatientId(item);
    setSelectedRowId(null);
  };

  const canTakePayment = homescreenprev.includes("Take Payment");

  return (
    <>
      <div className="bmsi-table">
        <div className="bmsi-scroll">
          <table>
            <thead>
              <tr>
                {sortHead("token", "Token")}
                <th>Provider</th>
                <th>Location</th>
                {sortHead("appt", "Appt time")}
                {sortHead("checkin", "Checked in")}
                {sortHead("patient", "Patient")}
                <th>Room</th>
                <th>Status</th>
                {printColumn && <th>Print</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="bmsi-empty">Loading…</td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="bmsi-empty">No records found</td>
                </tr>
              ) : (
                sorted.map((item) => {
                  const meta = statusMeta(item.visit_confirmation);
                  const checkin = checkinAt(item);
                  const wait = live && item.visit_confirmation !== "Checked Out"
                    ? waitMinutes(item, now)
                    : null;
                  const room = roomOf(item);
                  const printCount = item.signintime?.printCount || (item.signintime?.printedBy ? 1 : 0);
                  const providers = providersOf(item);
                  return (
                    <tr
                      key={item.appointmentid ?? item.id}
                      className={FLAGGED_STATUSES.includes(item.visit_confirmation) ? "flagged" : ""}
                    >
                      <td>
                        {/* The border keeps the coverage colour the old token
                            ring carried (deductiblemet). */}
                        <div
                          className="bmsi-token"
                          style={item.deductiblemet ? { borderColor: `#${item.deductiblemet}`, borderWidth: 2 } : undefined}
                        >
                          {item.token || "–"}
                        </div>
                      </td>

                      <td>
                        <div className="bmsi-provs">
                          {providers.map((name) => {
                            const proc = /procedure/i.test(name);
                            return (
                              <button
                                key={name}
                                type="button"
                                className={`bmsi-prov${proc ? " proc" : ""}`}
                                title={name}
                                onClick={() => openProviders(item)}
                              >
                                {proc ? <PulseIcon /> : <StethoscopeIcon />}
                                <span>{name}</span>
                              </button>
                            );
                          })}
                          {providers.length === 0 && <span className="bmsi-dash">—</span>}
                        </div>
                      </td>

                      <td className="bmsi-location">
                        {item?.signintime?.checkinFrom || <span className="bmsi-dash">—</span>}
                      </td>

                      <td className="bmsi-time">{formatApptTime(item.starttime)}</td>

                      <td>
                        {checkin ? (
                          <>
                            <div className="bmsi-time">{checkin.format("h:mm A")}</div>
                            {wait != null && (
                              <div className={`bmsi-wait${wait >= 60 ? " long" : ""}`}>
                                Waiting {formatWait(wait)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="bmsi-notyet">Not yet</span>
                        )}
                      </td>

                      <td>
                        <div className="bmsi-patient">
                          <span className="bmsi-avatar" style={avatarTone(item)}>
                            {initials(item)}
                          </span>
                          <div style={{ minWidth: 0 }}>
                            <button
                              type="button"
                              className="bmsi-pname"
                              onClick={() => navigate("/landing", { state: { item } })}
                            >
                              {`${item.firstname} ${item.lastname}`}
                              {item.ordertype === "Labs" && <img src={labsIcon} alt="Labs" />}
                            </button>
                            <div className="bmsi-pid">#{item.patientid}</div>
                          </div>
                        </div>
                      </td>

                      <td>
                        {room ? (
                          <button
                            type="button"
                            className={`bmsi-room${room === "Waiting Room" ? " waiting" : ""}`}
                            title={room}
                            onClick={() => openRoom(item)}
                          >
                            {room === "Waiting Room" ? "Waiting" : room.slice(0, 3)}
                          </button>
                        ) : (
                          <button type="button" className="bmsi-assign" onClick={() => openRoom(item)}>
                            Assign
                          </button>
                        )}
                      </td>

                      <td>
                        <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          className={`bmsi-status tone-${meta.tone}`}
                          onClick={() => openStatus(item)}
                        >
                          <span className="bmsi-status-dot" />
                          {meta.label}
                          <ChevronDown />
                        </button>
                        {!printColumn && canTakePayment && (
                          <div className="bmsi-more">
                            <DropdownMenu
                              item={item}
                              selectedRowId={selectedRowId}
                              setSelectedRowId={setSelectedRowId}
                              openPaymentModal={openPaymentModal}
                            />
                          </div>
                        )}
                        </div>
                      </td>

                      {printColumn && (
                      <td>
                        <div className="bmsi-printcell">
                          <button
                            type="button"
                            className={`bmsi-print${printCount ? " re" : ""}`}
                            disabled={printingId === item.patientid}
                            title={
                              item.signintime?.printedBy
                                ? `Printed by ${item.signintime.printedBy}${
                                    item.signintime.printedAt
                                      ? " · " + dayjs(item.signintime.printedAt).format("M/D h:mm A")
                                      : ""
                                  }`
                                : undefined
                            }
                            onClick={(e) => handlePrintEncounter(e, item)}
                          >
                            <PrinterIcon />
                            {printingId === item.patientid ? "Printing…" : printCount ? "Reprint" : "Print"}
                            {printCount > 0 && printingId !== item.patientid && (
                              <span className="bmsi-print-n">{printCount}×</span>
                            )}
                          </button>
                          <div className="bmsi-more">
                            {canTakePayment && (
                              <DropdownMenu
                                item={item}
                                selectedRowId={selectedRowId}
                                setSelectedRowId={setSelectedRowId}
                                openPaymentModal={openPaymentModal}
                              />
                            )}
                          </div>
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ProvidersPopup
        getproviders={getproviders}
        doctorNames={providersdata}
        isOpen={isProvidersPopup}
        onClose={() => setIsprovidersPopup(false)}
      />
      <StatusPopup
        updateStatus={updateStatus}
        isOpen={isStatusPopup}
        onClose={() => setIsStatusPopup(false)}
      />
      <RoomPopup
        updateRoom={updateRoom}
        isOpen={roomPopUp}
        onClose={() => setRoomPopUp(false)}
        rowdata={rowdata}
        fetchdata={fetchdata}
        data={allRows}
      />
      {paymentModal ? (
        <Payments
          data={selectedPatientId}
          alldata={allRows}
          fetchdata={fetchdata}
          isOpen={paymentModal}
          ClosePaymentModal={ClosePaymentModal}
        />
      ) : null}
    </>
  );
};

export default SigninTable;
